import React, { useMemo, useState } from "react";
import productImg from "../../assets/images/product1.png";
import { buildModelNumber } from "../../utils/productNomenclature";

const PreviewSectionHeader = ({ title, sectionKey, isOpen, onToggle }) => (
    <button
        type="button"
        className="preview-panel-section-header"
        onClick={() => onToggle(sectionKey)}
        aria-expanded={isOpen}
    >
        <h6>{title}</h6>
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true"></i>
    </button>
);

const PreviewEmptyState = ({ children = "No data available" }) => (
    <div className="preview-panel-empty">
        <i className="fas fa-info-circle" aria-hidden="true"></i>
        <span>{children}</span>
    </div>
);

const formatLabel = (key) =>
    key
        ?.toString()
        ?.replace(/([a-z])([A-Z])/g, "$1 $2")
        ?.replace(/[_-]+/g, " ")
        ?.replace(/\s+/g, " ")
        ?.trim()
        ?.replace(/^./, (char) => char.toUpperCase()) || "Item";

const normalizePreviewRows = (source) => {
    if (!source) return [];

    if (Array.isArray(source)) {
        return source
            .map((item) => {
                if (typeof item !== "object" || item === null) {
                    return { label: "Value", value: item };
                }

                return {
                    label: item.label || item.name || item.title || item.attributeName || item.parameterName || item.key,
                    value: item.value || item.attributeValue || item.parameterValue || item.description || item.fileName,
                };
            })
            .filter((item) => item?.label || item?.value);
    }

    if (typeof source === "object") {
        return Object.entries(source)
            .filter(([, value]) => value !== null && value !== undefined && value !== "")
            .map(([key, value]) => ({ label: formatLabel(key), value }));
    }

    return [{ label: "Value", value: source }];
};

const PreviewDataList = ({ data, emptyText = "No data available" }) => {
    const rows = normalizePreviewRows(data);

    if (rows.length === 0) {
        return <PreviewEmptyState>{emptyText}</PreviewEmptyState>;
    }

    return (
        <div className="preview-panel-data-list">
            {rows.map((row, index) => (
                <div className="preview-panel-data-row" key={`${row?.label || "row"}-${index}`}>
                    <span>{row?.label || "Item"}</span>
                    <strong>{Array.isArray(row?.value) ? row.value.join(", ") : row?.value}</strong>
                </div>
            ))}
        </div>
    );
};

const PreviewFileList = ({ files, emptyText }) => (
    <>
        {files.length > 0 ? (
            files.map((file, idx) => (
                <a
                    key={`${file?.fileUrl || file?.label || "file"}-${idx}`}
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="preview-panel-file-row"
                >
                    <i className="fas fa-file-pdf" aria-hidden="true"></i>
                    <span>{file.label}</span>
                    <i className="fas fa-download ml-auto" aria-hidden="true"></i>
                </a>
            ))
        ) : (
            <PreviewEmptyState>{emptyText}</PreviewEmptyState>
        )}
    </>
);

export const ProductPreviewPanel = ({
    productConfig,
    configSetting = [],
    attributeDetails = [],
    balancingFactors,
    drawingFiles,
    onSelectSimilarModel,   // optional callback(productId) — wire later if needed
}) => {
    const [openSections, setOpenSections] = useState({
        productData: false,
        performanceData: false,
        drawing: false,
        balancingFactors: false,
    });


    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const imageUrl = productConfig?.productName?.imageUrl || productImg;
    const productLabel = productConfig?.productName?.productName || "";

    const modelNo = useMemo(
        () => buildModelNumber({ productConfig, configSetting, attributeDetails }),
        [productConfig, configSetting, attributeDetails]
    );

    const selectedConfig = useMemo(
        () => configSetting
            .filter((c) => c?.attributeName && c?.valueName)
            .map((item) => ({
                ...item,
                displayValue: item?.specialValue
                    ? `${item.valueName} (${item.specialValue})`
                    : item.valueName,
            })),
        [configSetting]
    );


    // Similar models — match products whose nomenclature starts with the
    // same generated code (e.g. "TA1V2" matches "TA1V2" and "TA1V2-34")
    const similarModels = useMemo(() => {
        if (!modelNo) return [];
        const currentProductId = productConfig?.productName?.productId;
        const seen = new Map();

        attributeDetails.forEach((row) => {
            const code = row?.productNomenclature;
            if (!code) return;
            if (!code.toUpperCase().startsWith(modelNo.toUpperCase())) return;
            if (row.productId === currentProductId) return;
            if (!seen.has(row.productId)) {
                seen.set(row.productId, {
                    id: row.productId,
                    code,
                    productName: row.productName,
                });
            }
        });

        return Array.from(seen.values());
    }, [modelNo, attributeDetails, productConfig?.productName?.productId]);

    const resolvedBalancingFactors =
        balancingFactors ?? productConfig?.productName?.balancingFactors ?? [];
    const resolvedDrawingFiles =
        drawingFiles ?? productConfig?.productName?.drawingFiles ?? [];
    const resolvedProductData =
        productConfig?.productName?.productData ?? productConfig?.productName?.technicalData ?? [];
    const resolvedPerformanceData =
        productConfig?.productName?.performanceData ?? productConfig?.productName?.performance ?? [];

    return (
        <div className="product-preview-panel">

            <div className="preview-panel-card">
                {/* Header row — "Selected Model" label left, model code right */}
                <div className="preview-panel-model-header">
                    <span className="preview-panel-model-label">
                        Selected Model : <strong>{productLabel || "Not selected"}</strong>
                    </span>
                </div>

                {/* Image + product name, stacked & centered */}
                <div className="preview-panel-image-row">
                    <img src={imageUrl} alt={productLabel || "Product"} />
                    {selectedConfig.length > 0 && (
                        <div className="preview-panel-nomenclature">
                            <div className="nomenclature-body">
                                {selectedConfig.map((item, idx) => (
                                    <React.Fragment key={idx}>
                                        <span className="nomenclature-name">{item.attributeName}: </span>
                                        <span className="nomenclature-value">{item.displayValue}</span>
                                        {idx < selectedConfig.length - 1 && (
                                            <span className="nomenclature-sep">&rsaquo;</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>


                        </div>
                    )}
                </div>

                {/* Nomenclature breakdown — title + chained raw values with › separators */}


                {similarModels.length > 0 && (
                    <div className="preview-panel-similar">
                        <span className="meta-label">Similar models</span>
                        <div className="preview-panel-similar-list">
                            {similarModels.map((model) => (
                                <button
                                    key={model.id}
                                    type="button"
                                    className="preview-panel-similar-chip"
                                    title={model.productName}
                                    onClick={() => onSelectSimilarModel && onSelectSimilarModel(model)}
                                >
                                    {model.code}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Accordion sections — unchanged, kept as-is below the overview */}
            <div className="preview-panel-card">
                <PreviewSectionHeader
                    title="Product Data"
                    sectionKey="productData"
                    isOpen={openSections.productData}
                    onToggle={toggleSection}
                />
                {openSections.productData && (
                    <div className="preview-panel-body">
                        <PreviewDataList data={resolvedProductData} emptyText="No product data available" />
                    </div>
                )}
            </div>

            <div className="preview-panel-card">
                <PreviewSectionHeader
                    title="Performance Data"
                    sectionKey="performanceData"
                    isOpen={openSections.performanceData}
                    onToggle={toggleSection}
                />
                {openSections.performanceData && (
                    <div className="preview-panel-body">
                        <PreviewDataList data={resolvedPerformanceData} emptyText="No performance data available" />
                    </div>
                )}
            </div>

            <div className="preview-panel-card">
                <PreviewSectionHeader
                    title="Drawing"
                    sectionKey="drawing"
                    isOpen={openSections.drawing}
                    onToggle={toggleSection}
                />
                {openSections.drawing && (
                    <div className="preview-panel-body">
                        <PreviewFileList files={resolvedDrawingFiles} emptyText="No files available" />
                    </div>
                )}
            </div>

            <div className="preview-panel-card">
                <PreviewSectionHeader
                    title="Balancing Factors"
                    sectionKey="balancingFactors"
                    isOpen={openSections.balancingFactors}
                    onToggle={toggleSection}
                />
                {openSections.balancingFactors && (
                    <div className="preview-panel-body">
                        <PreviewFileList files={resolvedBalancingFactors} emptyText="No files available" />
                    </div>
                )
                }
            </div >

        </div >
    );
};

export default ProductPreviewPanel;
